<template>
  <div data-component-group="business-form" data-component="labels">
    <slot name="beforeForm" />

    <div v-if="!isDisabled" class="component-header">
      <magic-container>
          <v-autocomplete
            v-model="country"
            :items="threadSubmissions"
            prepend-inner-icon="public"
            label="Country"
            item-title="name"
            item-value="code"
            :return-object="true"
            required
            :readonly="isDisabled"
            :error-messages="countryErrors"
            :hint="$t('labels_country_hint')"
            persistent-hint
            @update:model-value="onChangeCountry"
            @blur="v$.country.$touch()"
          />
          <v-autocomplete
            v-model="selectedLabels"
            :items="labels"
            :multiple="true"
            :loading="isLoadingLabels"
            prepend-inner-icon="public"
            label="Labels"
            item-title="name"
            item-value="id"
            :return-object="true"
            :readonly="isDisabled"
            :disabled="!country"
            v-model:search="labelSearch"
            :error-messages="labelErrors"
            @blur="v$.selectedLabels.$touch()"
          />
      </magic-container>

      <div class="component-actions">
        <v-btn
          color="secondary"
          :loading="isLoading"
          @click="onUpdateLabels">
          <v-icon>edit</v-icon>
          {{ triggerLabel }}
        </v-btn>
      </div>
    </div>
    <div class="component-body">
      <Removables
        v-if="hasLabels"
        :items="businessLabels"
        :is-disabled="isDisabled"
        :on-remove="onRemoveLabels"
      >
        <template v-slot:item="scope" >
          <div class="d-flex align-center mr-5">
            <v-icon>public</v-icon>
            {{ scope.item.country.name }}
          </div>
          <div class="labels">
            <span
              v-for="label in scope.item.labels"
              :key="label.id"
              class="label"
            >
              <v-icon> label </v-icon>
              {{ label.name }}
            </span>
          </div>
        </template>
      </Removables>
    </div>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core'
  import { required } from '@vuelidate/validators';
  import { v1 as uuidv1 } from 'uuid';
  import Removables from '../../lists/Removables';
  import { isObj, isStr, debounce } from '../../../utils';
  import { find } from 'lodash-es';

  const debouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  export default {
    components: {
      Removables,
    },
    setup () {
    return {
      v$: useVuelidate()
    }
  },
    validations() {
      return {
        country: {
          required,
        },
        selectedLabels: {
          required,
        },
      };
    },
    props: {
      business: {
        type: Object,
        default: undefined,
      },
      triggerLabel: {
        type: String,
        default: 'Attach',
      },
      isDisabled: {
        type: Boolean,
        default: false,
      },
      onSubmit: {
        type: Function,
        default: () => {},
      },
    },
    data() {
      return {
        country: null,
        selectedLabels: [],
        labelSearch: '',
        isLoading: false,
        isLoadingLabels: false,
        isRefreshingLabels: false,
      };
    },
    computed: {
      userCountries() {
        return this.$store.getters['users/countries'];
      },
      threadSubmissions() {
        const { threadSubmissions } = this.$store.getters['businesses/activeItem'] || {};

        return threadSubmissions?.filter((item) => {
          return find(this.userCountries, { code: item.code });
        });
      },
      businessLabels() {
        const { labels } = this.$store.getters['businesses/activeItem'];
        return this.transformLabels(labels);
      },
      labels() {
        return this.$store.getters['labels/items'];
      },
      hasLabels() {
        return this.businessLabels.length > 0;
      },
      countryErrors() {
        return this.v$.country.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Country is required';
          }
          return error.$message;
        });
      },
      labelErrors() {
        return this.v$.selectedLabels.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Label is required';
          }
          return error.$message;
        });
      },
    },
    watch: {
      business: {
        immediate: true,
        handler(nextBusiness, prevBusiness) {
          if (isObj(nextBusiness) && nextBusiness.id) {
            if (prevBusiness !== undefined) {
              this.clear();
            }
            this.getLabels(nextBusiness);
          }
        }
      },
      selectedLabels() {
        this.isRefreshingLabels = true;
      },
      labelSearch(nextSearch) {
        if (isStr(nextSearch)) {
          if (this.isRefreshingLabels) {
            this.isRefreshingLabels = false;
            return false;
          }

          debouncer(() => this.searchLabels(nextSearch));
        }
      },
    },
    methods: {
      onChangeCountry(country) {
        this.selectedLabels = [];
        const query = {
          countries: [country.code],
          perPage: -1,
        };

        return this.$store.dispatch('labels/getItems', query);
      },
      onUpdateLabels() {
        this.v$.$touch();

        if (!this.canSubmitRequest() || this.v$.$error || this.v$.pending) {
          return false;
        }

        this.isLoading = true;
        const { country, selectedLabels, onSubmit } = this;

        onSubmit({
          country,
          labels: selectedLabels,
        }).then((res) => {
          this.clear();
          this.getLabels(this.business);
          return res;
        })
          .catch(err=>err)
          .finally(()=>{
            this.isLoading = false;
          });
      },
      canSubmitRequest() {
        const { country, labels } = this;

        return isStr(country?.code) && labels.length > 0;
      },
      getLabels({ id }) {
        const query = { id };
        return this.$store.dispatch('businesses/getLabels', query);
      },
      searchLabels(search = '') {
        const query = {
          search,
        };

        this.isLoadingLabels = true;
        return this.$store.dispatch('labels/getItems', query).then((res) => {
          this.isLoadingLabels = false;
          return res;
        });
      },
      onRemoveLabels(labelGroup) {
        const query = {
          id: this.business.id,
          country: labelGroup.country.code,
        };

        return this.$store.dispatch('businesses/removeLabels', query);
      },
      transformLabels(labels) {
        let nextLabels = [];

        labels.forEach((label) => {
          const { country } = label;
          const isExistingCountry =
            nextLabels.length > 0 &&
            nextLabels.filter((l) => l.country.code === country.code).length > 0;

          if (isExistingCountry) {
            nextLabels = nextLabels.map((l) => ({
              ...l,
              labels:
                l.country.code === country.code ? [...l.labels, label] : l.labels,
            }));
          } else {
            nextLabels.push({
              country,
              labels: [label],
            });
          }
        });

        return nextLabels;
      },
      clear() {
        this.v$.$reset();
        this.country = null;
        this.selectedLabels = [];
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

[data-component-group='business-form'][data-component='labels'] {
  padding: $spacing-xl;
  background-color: map.get($colors, shades, 'white');
  border-top: 1px solid map.get($colors, grey, lighten-2);

  .component-actions {
    text-align: right;
    padding: 0 0 16px 0;

    .v-btn {
      margin: 0;

      .v-icon {
        margin: 0 6px 0 0;
      }
    }
  }

  .labels {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
  }

  .label {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    font-size: $text-size-sm;
    padding: 2px 6px;
    margin: 0 10px 4px 0;
    border-radius: $border-radius;
    color: map.get($colors, shades, 'white');
    background-color: map.get($colors, primary);

    .v-icon {
      font-size: $text-size-md;
      margin: 0 6px 0 0;
      color: map.get($colors, shades, 'white');
    }
  }

  [data-component='removables'] {
    .v-list {
      padding: 0;
    }

    .v-list-item {
      height: auto;
      padding: 12px 16px;
      border-radius: $border-radius;
      background-color: map.get($colors, grey, lighten-3);
    }
  }
}
</style>
